/**
 * Discount-aware display total for the POS cart + checkout. (T-356 / T-370)
 *
 * SSOT note: the committed sale subtracts promotion AND loyalty redemption from the
 * cart total in the backend `settleSaleTotals` (sale-totals.ts), sequentially and
 * each clamped at zero:
 *   totalAmount = subtotal − orderDiscount − promotion − loyalty + tax
 * The pre-payment display must mirror that exact, clamped, sequential subtraction so
 * the Total shown to the cashier equals the Total the sale actually charges. This
 * helper is the single client-side owner of that subtraction — CartPanel and
 * CheckoutDialog both call it rather than each doing their own math.
 *
 * @param grandTotal       cart grand total BEFORE promotions/loyalty (subtotal − order
 *                         discount + applicable tax), from the cart store.
 * @param promotionDiscount total auto-applied promotion discount for the cart, from the
 *                         `/promotions/preview` SSOT (see usePromotionPreview).
 * @param loyaltyDiscount  server-applied loyalty redemption discount (pre-tax capped +
 *                         whole-point quantized) from the `/sales/preview` SSOT (see
 *                         useLoyaltyRedeemPreview). CartPanel passes 0 — loyalty is
 *                         redeemed only at checkout.
 */
export function applyDiscountsToTotal(
  grandTotal: number,
  promotionDiscount: number,
  loyaltyDiscount: number,
): number {
  let net = grandTotal;
  net -= promotionDiscount > 0 ? promotionDiscount : 0;
  if (net < 0) net = 0;
  net -= loyaltyDiscount > 0 ? loyaltyDiscount : 0;
  if (net < 0) net = 0;
  return net > 0 ? Math.round(net * 100) / 100 : 0;
}

/**
 * Promotion-only total. Kept for CartPanel (loyalty is checkout-only) — delegates to the
 * single owner `applyDiscountsToTotal` with loyaltyDiscount = 0 so there is one subtraction
 * owner, not two. (T-356)
 */
export function applyPromotionToTotal(grandTotal: number, promotionDiscount: number): number {
  return applyDiscountsToTotal(grandTotal, promotionDiscount, 0);
}
